import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Wrench, Sparkles, ShieldCheck, Zap, Star, MessageCircle, Smartphone, Tablet, Laptop, Watch, Gamepad2, MapPin, Phone, Navigation } from "lucide-react";
import { STORE, waLink, mapsLink, openAssistant, fetchRepairsConfig } from "@/lib/store";
import { OffersCarousel } from "@/components/OffersCarousel";
import { UsedShowcase } from "@/components/UsedShowcase";
import { Seo } from "@/components/Seo";

const STATS = [
  { value: "10.000+", label: "Clienti Soddisfatti" },
  { value: "3.000+", label: "Dispositivi Venduti" },
  { value: "12.000+", label: "Riparazioni Effettuate" },
];

const REVIEWS = [
  "Tecnico bravo e disponibilissimo, ha risolto in due minuti un problema al mio iPhone!",
  "Proprietario gentilissimo, prezzi ottimi in concorrenza con Amazon.",
  "Personale molto professionale. Telefono riportato come nuovo in meno di 24h.",
  "Ottimo negozio! Riparazioni express, attenti ai minimi dettagli. Consiglio!",
];

const DEVICES = [
  { icon: Smartphone, t: "Smartphone" }, { icon: Tablet, t: "Tablet" },
  { icon: Laptop, t: "Computer & Mac" }, { icon: Watch, t: "Smartwatch" }, { icon: Gamepad2, t: "Console" },
];

const VALUE_PROPS = [
  { icon: Zap, t: "Riparazioni Express", d: "Interventi rapidi, diagnosi immediata e molte riparazioni completate in giornata." },
  { icon: ShieldCheck, t: "Componenti Selezionati", d: "Ricambi di grado originale e OEM certificati per la massima longevità del dispositivo." },
  { icon: Star, t: "Tecnici Qualificati", d: "Dispositivi sempre in mani competenti, con strumenti di precisione dedicati." },
];

// simple-icons slugs per i marchi realmente gestiti (Realme non ha un logo affidabile -> fallback testuale)
const BRAND_SLUGS = { apple: "apple", samsung: "samsung", xiaomi: "xiaomi", huawei: "huawei", oppo: "oppo", honor: "honor", realme: null };
const DEFAULT_BRANDS = ["Apple", "Samsung", "Xiaomi", "Huawei", "OPPO", "Honor", "Realme"];
const kebab = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

function BrandLogo({ name }) {
  const slug = BRAND_SLUGS[name.toLowerCase()];
  const [failed, setFailed] = useState(!slug);
  if (failed || !slug)
    return <span className="font-display text-xl font-extrabold tracking-tight text-slate-800 transition-colors group-hover:text-blue-600">{name}</span>;
  return (
    <img
      src={`https://cdn.simpleicons.org/${slug}/0F172A`}
      alt={name}
      loading="lazy"
      onError={() => setFailed(true)}
      className="h-8 w-auto max-w-[130px] object-contain opacity-85 transition-all duration-300 group-hover:scale-110 group-hover:opacity-100 sm:h-9"
    />
  );
}

export default function Home() {
  const [brands, setBrands] = useState(DEFAULT_BRANDS);

  useEffect(() => {
    fetchRepairsConfig()
      .then((c) => {
        const list = (c?.brands || []).filter((b) => b && b.toLowerCase() !== "altro");
        if (list.length) setBrands(list);
      })
      .catch(() => {});
  }, []);

  return (
    <main data-testid="home-page" className="bg-[#FAFCFF]">
      <Seo route="/" />
      {/* 1 — HERO */}
      <section className="relative overflow-hidden border-b border-slate-100 bg-gradient-to-b from-blue-50/50 via-white to-white">
        <div className="pointer-events-none absolute -right-40 -top-40 h-96 w-96 rounded-full bg-blue-200/30 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-24">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-blue-200/80 bg-blue-50/80 px-3.5 py-1.5 font-mono text-xs uppercase tracking-widest text-blue-700 shadow-sm backdrop-blur-sm">
              <MapPin className="h-3 w-3" /> {STORE.city}
            </span>
            <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
              Vendita e Riparazione<br />sempre al tuo servizio
            </h1>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-slate-600 sm:text-lg">
              Il negozio di dispositivi elettronici a {STORE.city}: smartphone, tablet, computer e accessori.
              Vuoi sapere <strong className="text-slate-800">prezzo e disponibilità</strong>? Chiedilo al nostro assistente AI: usa le offerte e i listini IPHIX disponibili.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button data-testid="hero-ai-btn" onClick={openAssistant} className="inline-flex items-center gap-2.5 rounded-full bg-blue-600 px-7 py-3.5 font-semibold text-white shadow-lg shadow-blue-500/25 transition-all duration-300 hover:scale-[1.02] hover:bg-blue-700 hover:shadow-blue-500/40 active:scale-[0.98]">
                <Sparkles className="h-5 w-5" /> Chiedi i prezzi all'assistente AI
              </button>
              <Link data-testid="hero-repairs-btn" to="/riparazioni" className="inline-flex items-center gap-2.5 rounded-full border border-slate-200 bg-white/90 px-7 py-3.5 font-semibold text-slate-800 shadow-sm backdrop-blur transition-all duration-300 hover:scale-[1.02] hover:border-blue-400 hover:bg-blue-50/50 hover:text-blue-600 active:scale-[0.98]">
                <Wrench className="h-5 w-5" /> Prenota Riparazione
              </Link>
              <Link data-testid="hero-valuation-btn" to="/valuta-iphone" className="inline-flex items-center gap-2.5 rounded-full bg-slate-900 px-7 py-3.5 font-semibold text-white shadow-lg shadow-slate-900/20 transition-all duration-300 hover:scale-[1.02] hover:bg-slate-800 hover:shadow-slate-900/30 active:scale-[0.98]">
                <Smartphone className="h-5 w-5" />
                <span className="sm:hidden">Valuta il tuo iPhone</span>
                <span className="hidden sm:inline">Vendi / Valuta il tuo iPhone</span>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2 — CAROSELLO PROMOZIONI */}
      <OffersCarousel />
      <UsedShowcase />

      {/* 3 — ASSISTENTE AI SPOTLIGHT */}
      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:py-16">
        <div className="relative overflow-hidden rounded-[2rem] border border-blue-400/20 bg-gradient-to-br from-[#060D1E] via-[#0B1E48] to-[#123173] p-8 text-white shadow-2xl shadow-blue-950/40 sm:p-12 lg:p-16">
          <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-blue-500/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-indigo-500/15 blur-3xl" />
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.18),transparent_70%)]" />
          <div className="relative max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-blue-400/30 bg-blue-500/15 px-3.5 py-1 font-mono text-xs font-medium tracking-widest text-blue-200 backdrop-blur-md">
              <Sparkles className="h-3 w-3" /> Assistente AI
            </span>
            <h2 className="mt-5 font-display text-3xl font-extrabold leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl">Prezzi e offerte in un attimo</h2>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-blue-100/80 sm:text-lg">
              Chiedi al nostro assistente AI il prezzo di uno smartphone, tablet o altro prodotto. L'AI utilizza le offerte e i listini IPHIX disponibili.
            </p>
            <button data-testid="spotlight-ai-btn" onClick={openAssistant} className="mt-8 inline-flex items-center gap-3 rounded-full bg-white px-8 py-4 font-display text-base font-bold text-slate-950 shadow-xl shadow-black/20 transition-all duration-300 hover:scale-[1.02] hover:bg-blue-50 hover:text-blue-900 active:scale-[0.98]">
              <Sparkles className="h-5 w-5" /> Chiedi ora
            </button>
          </div>
        </div>
      </section>

      {/* 4 — PRENOTA RIPARAZIONE */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="relative overflow-hidden rounded-[2rem] border border-blue-100 bg-gradient-to-br from-blue-50/60 via-white to-slate-50 p-8 shadow-sm sm:p-12 lg:p-14">
          <div className="max-w-2xl">
            <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">Hai un dispositivo da riparare?</h2>
            <p className="mt-3 text-slate-600 sm:text-lg">Diagnosi rapida, ricambi certificati e tecnici qualificati. Richiedi subito un preventivo: la richiesta viene verificata dal nostro staff prima della conferma.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link data-testid="repairs-cta-btn" to="/riparazioni" className="inline-flex items-center gap-2.5 rounded-full bg-blue-600 px-7 py-3.5 font-semibold text-white shadow-md shadow-blue-500/20 transition-all duration-300 hover:scale-[1.02] hover:bg-blue-700 hover:shadow-lg active:scale-[0.98]">
                <Wrench className="h-5 w-5" /> Richiedi Preventivo
              </Link>
              <a data-testid="repairs-whatsapp-btn" href={waLink("Ciao iPhix, vorrei una riparazione.")} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2.5 rounded-full border border-emerald-200 bg-emerald-50 px-7 py-3.5 font-semibold text-emerald-800 transition-all duration-300 hover:scale-[1.02] hover:border-[#25D366] hover:bg-[#25D366] hover:text-white active:scale-[0.98]">
                <MessageCircle className="h-5 w-5" /> WhatsApp
              </a>
              <a data-testid="repairs-directions-btn" href={mapsLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2.5 rounded-full border border-slate-200 bg-white px-7 py-3.5 font-semibold text-slate-800 shadow-sm transition-all duration-300 hover:scale-[1.02] hover:border-blue-400 hover:text-blue-600 active:scale-[0.98]">
                <Navigation className="h-5 w-5" /> Indicazioni stradali
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 5 — STATISTICHE */}
      <section data-testid="stats-section" className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
        <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-3">
          {STATS.map((s) => (
            <div key={s.label} className="group relative overflow-hidden rounded-2xl border border-slate-200/70 bg-white/80 p-6 text-center shadow-sm backdrop-blur transition-all duration-300 hover:-translate-y-1 hover:border-blue-300 hover:shadow-md hover:shadow-blue-500/10">
              <div className="bg-gradient-to-r from-slate-900 to-blue-900 bg-clip-text font-display text-3xl font-extrabold tracking-tight text-transparent transition-all group-hover:from-blue-600 group-hover:to-indigo-600 sm:text-4xl">{s.value}</div>
              <div className="mt-1.5 font-mono text-[11px] uppercase leading-tight tracking-wide text-slate-500">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* 6 — CATEGORIE PRODOTTI / COSA RIPARIAMO */}
      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6">
        <span className="font-mono text-xs font-semibold uppercase tracking-widest text-blue-600">Servizi di Riparazione</span>
        <h2 className="mt-2 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">Cosa Ripariamo</h2>
        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {DEVICES.map((d) => (
            <div key={d.t} className="group flex flex-col items-center gap-3.5 rounded-2xl border border-slate-200/80 bg-white p-7 text-center transition-all duration-300 hover:-translate-y-1 hover:border-blue-400 hover:shadow-lg hover:shadow-blue-500/10">
              <d.icon className="h-10 w-10 text-slate-700 transition-transform duration-300 group-hover:scale-110 group-hover:text-blue-600" />
              <span className="font-display text-sm font-semibold text-slate-800 transition-colors group-hover:text-blue-600 sm:text-base">{d.t}</span>
            </div>
          ))}
        </div>
      </section>

      {/* 7 — MARCHI CHE RIPARIAMO */}
      <section data-testid="brands-section" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-20">
        <div className="max-w-2xl">
          <span className="font-mono text-xs font-semibold uppercase tracking-widest text-blue-600">Compatibilità Totale</span>
          <h2 className="mt-2 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">Marchi Che Ripariamo</h2>
          <p className="mt-3 text-slate-600 sm:text-lg">Ripariamo smartphone, tablet e computer dei principali marchi.</p>
        </div>
        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
          {brands.map((b) => (
            <Link
              key={b}
              to="/riparazioni"
              data-testid={`brand-card-${kebab(b)}`}
              className="group flex h-28 items-center justify-center rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-blue-500/60 hover:shadow-xl hover:shadow-blue-500/10 active:scale-[0.98] sm:h-32"
            >
              <BrandLogo name={b} />
            </Link>
          ))}
        </div>
      </section>

      {/* 8 — SERVIZI / PUNTI DI FORZA */}
      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6">
        <div className="grid gap-6 md:grid-cols-3">
          {VALUE_PROPS.map((f) => (
            <div key={f.t} className="group rounded-2xl border border-slate-200/80 bg-white p-7 shadow-sm transition-all duration-300 hover:border-blue-300 hover:shadow-md">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-blue-600 transition-colors duration-300 group-hover:bg-blue-600 group-hover:text-white">
                <f.icon className="h-6 w-6" />
              </div>
              <h3 className="mt-5 font-display text-lg font-bold text-slate-900 sm:text-xl">{f.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-500">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* RECENSIONI */}
      <section data-testid="reviews-section" className="mx-auto max-w-7xl px-4 pb-16 sm:px-6">
        <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">Dicono di Noi</h2>
        <p className="mt-2 text-slate-500 sm:text-lg">La soddisfazione dei nostri clienti a {STORE.city} è la nostra migliore garanzia.</p>
        <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {REVIEWS.map((r, i) => (
            <div key={i} className="rounded-2xl border border-slate-200/70 bg-white p-6 shadow-sm transition-all duration-300 hover:border-blue-200 hover:shadow-md">
              <div className="mb-3 flex gap-0.5 text-amber-400">
                {Array.from({ length: 5 }).map((_, k) => <Star key={k} className="h-4 w-4 fill-current" />)}
              </div>
              <p className="text-sm italic leading-relaxed text-slate-600">"{r}"</p>
            </div>
          ))}
        </div>
      </section>

      {/* 9 — CONTATTI */}
      <section data-testid="store-info-section" className="border-t border-slate-200/80 bg-gradient-to-r from-slate-50 via-blue-50/20 to-slate-50">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-6 px-4 py-12 sm:px-6">
          <div>
            <h2 className="font-display text-xl font-bold text-slate-900 sm:text-2xl">Vieni a trovarci a {STORE.city}</h2>
            <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-500"><MapPin className="h-4 w-4 text-blue-500" /> {STORE.address || "Scafati (SA)"}</p>
          </div>
          <div className="flex flex-wrap gap-3">
            {STORE.phone && <a href={`tel:${STORE.phone}`} className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-800 shadow-sm transition-all hover:scale-[1.02] hover:border-blue-400 hover:text-blue-600"><Phone className="h-4 w-4" /> {STORE.phone}</a>}
            <a data-testid="directions-btn" href={mapsLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm shadow-blue-500/20 transition-all hover:scale-[1.02] hover:bg-blue-700"><Navigation className="h-4 w-4" /> Indicazioni stradali</a>
            <a href={waLink("Ciao iPhix!")} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-2.5 text-sm font-semibold text-white shadow-sm shadow-emerald-500/20 transition-all hover:scale-[1.02] hover:bg-[#20ba5a]"><MessageCircle className="h-4 w-4" /> WhatsApp</a>
          </div>
        </div>
      </section>
    </main>
  );
}
